import React from 'react';
import { useQuery } from 'react-query';
import { wireguardAPI } from '../services/api';
import toast from 'react-hot-toast';

const copyConfig = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label || 'WireGuard config'} copied`);
  } catch (err) {
    toast.error('Could not copy — select the config instead');
  }
};

const downloadConfig = (text, filename) => {
  const blob = new Blob([text], { type: 'application/x-wireguard-profile' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'airpulse-steam-shield.conf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  toast.success(`Downloading ${filename || 'profile'}`);
};

const ProfileCard = ({ profile }) => {
  if (!profile?.config) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-black/25 p-4 flex flex-col min-w-0 overflow-hidden h-full">
      <h4 className="text-white font-semibold leading-snug min-h-[2.75rem]">
        {profile.title}
      </h4>
      <p className="text-xs text-white/65 mt-1 line-clamp-4">{profile.subtitle}</p>
      {profile.clone_of ? (
        <p className="text-xs text-amber-200/90 mt-2">
          Same tunnel as Steam Shield verification. Import once on the phone.
        </p>
      ) : null}
      {profile.qr_svg ? (
        <div
          className="mt-3 mx-auto w-full max-w-[13rem] aspect-square bg-white rounded-lg p-2 overflow-hidden shrink-0 [&_svg]:block [&_svg]:w-full [&_svg]:h-full [&_svg]:max-w-full [&_svg]:max-h-full"
          dangerouslySetInnerHTML={{ __html: profile.qr_svg }}
        />
      ) : null}
      <div className="mt-4 grid grid-cols-1 gap-2">
        <button
          type="button"
          onClick={() => downloadConfig(profile.config, profile.filename)}
          className="text-sm px-3 py-2 rounded bg-purple-600 hover:bg-purple-700 text-white leading-tight min-w-0"
        >
          <span className="block">Download</span>
          <span className="block text-[11px] font-normal opacity-90 truncate">
            {profile.filename}
          </span>
        </button>
        <button
          type="button"
          onClick={() => copyConfig(profile.config, profile.title)}
          className="text-sm px-3 py-2 rounded bg-white/10 hover:bg-white/20 text-white"
        >
          Copy config
        </button>
      </div>
    </div>
  );
};

const WireGuardCard = () => {
  const { data, isLoading } = useQuery('wireguardMine', wireguardAPI.mine, {
    refetchInterval: (last) => (last?.is_wireguard_ready ? 15000 : 5000),
  });

  const ready = Boolean(data?.is_wireguard_ready && data?.config);
  const installing = Boolean(data?.installing) || data?.reason === 'installing';
  const noSession = data?.reason === 'no_session';
  const profiles = Array.isArray(data?.profiles) && data.profiles.length
    ? data.profiles
    : ready
      ? [
          {
            id: 'steam_shield',
            title: 'Steam Shield verification',
            subtitle: data?.destroy_hint || '',
            config: data.config,
            qr_svg: data.qr_svg,
            filename: data.config_filename,
          },
        ]
      : [];

  return (
    <div className="gaming-card rounded-lg p-6 space-y-3">
      <h3 className="text-xl font-semibold text-white mb-1">GPU VPN</h3>
      <p className="text-sm text-white/70">
        Two tunnels, three names. Phone cards are the same full IPv4 profile
        (Steam Shield + Play from phone). PC/TV/box is split-tunnel Moonlight
        only. One WireGuard profile at a time on a device. Browser Play does
        not use this.
      </p>

      {isLoading && !data ? (
        <p className="text-sm text-white/50">Checking this session…</p>
      ) : null}

      {noSession ? (
        <p className="text-sm text-white/70">
          Launch a GPU from the dashboard. When it is up, QR codes for your
          seat appear here. Friends on a shared box each get their own QR —
          never yours.
        </p>
      ) : null}

      <p className="text-sm text-amber-200/90">
        {data?.destroy_hint
          || 'When the GPU is destroyed, turn WireGuard off on your phone and delete Steam Shield verification. We cannot remove it remotely. A full tunnel to a dead GPU looks like no internet.'}
      </p>

      {installing && !ready ? (
        <div className="space-y-2">
          <p className="text-sm text-amber-200">
            Pulling WireGuard from the Airpulse registry (no Ubuntu apt on the
            GPU). Keep this page open — the QRs appear when the tunnel is
            listening.
          </p>
          {String(data?.last_error || '').toLowerCase().includes('pull') ? (
            <p className="text-xs text-white/55">
              Waiting for registry.airpulse.net to hand the wg-tools image to
              this GPU.
            </p>
          ) : null}
        </div>
      ) : null}

      {ready ? (
        <div className="space-y-3">
          {data.endpoint_rotated ? (
            <p className="text-sm text-amber-200">
              The GPU public IP changed. Delete the old tunnel in WireGuard and
              scan again.
            </p>
          ) : null}
          <p className="text-xs text-white/50">
            Endpoint {data.endpoint}
            {data.seat != null ? ` · seat ${data.seat}` : ''}
            {' · Moonlight host '}
            {data.moonlight_host || '10.88.0.1'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 items-stretch">
            {profiles.map((profile) => (
              <ProfileCard key={profile.id} profile={profile} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default WireGuardCard;
